const countedTrustBars = (window.drKieuCountedTrustBars ??= new Set());

class DrKieuTrustBar extends HTMLElement {
  constructor() {
    super();
    this.hasAnimated = false;
    this.counterParts = [];
    this.observer = null;
    this.animationFrame = null;
  }

  connectedCallback() {
    this.prepareCounters();

    const pageKey = `${window.location.pathname}${window.location.search}:${this.dataset.sectionId}`;
    if (countedTrustBars.has(pageKey) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.finishCounters();
      return;
    }

    if (!window.IntersectionObserver) {
      countedTrustBars.add(pageKey);
      this.animateCounters();
      return;
    }

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || this.hasAnimated) return;

        countedTrustBars.add(pageKey);
        this.observer?.disconnect();
        this.animateCounters();
      },
      {
        rootMargin: '0px 0px -10% 0px',
        threshold: 0.25,
      }
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();

    if (this.animationFrame) {
      window.cancelAnimationFrame(this.animationFrame);
    }
  }

  prepareCounters() {
    if (this.counterParts.length > 0) return;

    this.querySelectorAll('[data-dk-trust-count]').forEach((element) => {
      if (element.dataset.dkTrustPrepared === 'true') return;

      const value = element.textContent ?? '';
      const pattern = /[-+]?\d+(?:[.,]\d+)?/g;
      const fragment = document.createDocumentFragment();
      let previousIndex = 0;
      let match;

      while ((match = pattern.exec(value)) !== null) {
        fragment.append(value.slice(previousIndex, match.index));

        const counter = document.createElement('span');
        const target = this.parseTarget(match[0]);
        counter.className = 'dk-trust-bar__count-value';
        counter.textContent = this.formatValue(0, target);
        fragment.append(counter);

        this.counterParts.push({ element: counter, ...target });
        previousIndex = pattern.lastIndex;
      }

      if (previousIndex === 0) return;

      fragment.append(value.slice(previousIndex));
      element.replaceChildren(fragment);
      element.dataset.dkTrustPrepared = 'true';
    });
  }

  parseTarget(rawValue) {
    const separator = rawValue.includes(',') ? ',' : rawValue.includes('.') ? '.' : '';
    const decimalPlaces = separator ? rawValue.split(separator).at(-1).length : 0;
    const normalizedValue = separator ? rawValue.replace(separator, '.') : rawValue;

    return {
      decimalPlaces,
      rawValue,
      separator,
      value: Number(normalizedValue),
    };
  }

  formatValue(value, target) {
    if (!Number.isFinite(target.value)) return target.rawValue;

    const formatted = target.decimalPlaces > 0 ? value.toFixed(target.decimalPlaces) : String(Math.round(value));
    return target.separator === ',' ? formatted.replace('.', ',') : formatted;
  }

  finishCounters() {
    this.counterParts.forEach((counter) => {
      counter.element.textContent = counter.rawValue;
    });
  }

  animateCounters() {
    if (this.hasAnimated) return;

    this.hasAnimated = true;
    const duration = 1800;
    const startTime = window.performance.now();

    const update = (currentTime) => {
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);

      this.counterParts.forEach((counter) => {
        const currentValue = counter.value * easedProgress;
        counter.element.textContent =
          progress === 1 ? counter.rawValue : this.formatValue(currentValue, counter);
      });

      if (progress < 1) {
        this.animationFrame = window.requestAnimationFrame(update);
      } else {
        this.animationFrame = null;
      }
    };

    this.animationFrame = window.requestAnimationFrame(update);
  }
}

if (!customElements.get('dr-kieu-trust-bar')) {
  customElements.define('dr-kieu-trust-bar', DrKieuTrustBar);
}
